<template>
	<div class="custom-table">
		<xl-tabs v-model="searchData.status" :tabLists="tabs" v-if="tabs.length" @tab-change="handleSearch"></xl-tabs>
		<div class="custom-table-wrap">
			<div class="custom-table-header">
				<div class="flex">
					<search-form :class="isMore || !props.showMore ? 'height-auto' : 'h48'" v-model="searchData" :searchParams="searchParams" :checkedColumns="customColumns.checked"/>
					<div class="search-btns">
						<xl-button type="primary" size="large" @click="handleSearch">{{$t('button.search')}}</xl-button>
						<xl-button size="large" @click="handleReset">{{$t('button.reset')}}</xl-button>
						<xl-button v-if="props.showMore" size="large" @click="handleMore">
							<template v-if="!isMore">
								{{ $t( 'moreSearch.show' ) }}<ArrowDownBold class="arrow-icon"/>
							</template>
							<template v-else>
								<span class="hide-more">{{ $t( 'moreSearch.hide' ) }}<ArrowUpBold class="arrow-icon"/></span>
							</template>
						</xl-button>
					</div>
				</div>
				<div class="flex">
					<slot name="extra-btn"></slot>
					<xl-button size="large" @click="showTableCustomColumns" style="padding: 0 11px;min-width: 40px;">
						<XlBiaoge />
					</xl-button>
				</div>
			</div>
			<div class="custom-table-content" ref="tableWrapRef">
				<xl-table-header
					ref="tableRef"
					class="flex-full"
					height='100%'
					:headers="headers"
					v-model:customColumnsChecked="customColumns.checked" 
					v-model:customColumnsVisible="customColumns.visible"
					:customColumnsConf="customColumns.conf"
					:default-sort="config.defaultSort"
					:page="pageInfo.page"
					:data="tableData"
					:emptyText="$t('common.noData')"
					@submitCustomColumns="submitCustomColumns" 
					@sortChange="changeTableSort"
					@openOperation="openOperation"
					:needNum="6"
				>
					<template #popButtonContent>
						<div class="custom-button"></div>
					</template>
				</xl-table-header>
			</div>
			<xl-pagination
				v-model:page="pageInfo.page"
				v-model:pageSize="pageInfo.pageSize"
				:total="pageInfo.total"
				@pageSizeChange="handleSearch"
				@pageChange="handleSearch">
			</xl-pagination>
		</div>
	</div>
</template>

<script setup>
import { XlTabs, XlTableHeader, XlButton, XlPagination, XlIcon, XlMessage, XlLoading } from '@shining3d/ui-components';
import { Table, XlBiaoge, ArrowDownBold, ArrowUpBold } from '@shining3d/icons-vue';
import searchForm from './searchForm.vue';
import { $getLocalStorage, $setLocalStorage } from '@shining3d/common-module/utils/index.js';
import { useLang } from '@shining3d/common-module/utils/lang';
import unionBy from 'lodash/unionBy';
import { ref, nextTick } from 'vue';
const { $t } = useLang( '@shining3d/app' );

const props = defineProps({
	name: {
		type: String,
		default: '',
		required: true
	},
	config: {
		type: Object,
		default: () => ({}),
		required: true
	},
	tabs: {
		type: Array,
		default: () => []
	},
	headers: {
		type: Array,
		default: () => [],
		required: true
	},
	searchParams: {
		type: Array,
		default: () => []
	},
	showMore: {
		type: Boolean,
	}
});

const emits = defineEmits(['operate']);

const formatDefaultData = ( obj = {}, key = 'init' ) => props.searchParams.reduce(( acc, current ) => {
	acc[ current.key ] = current.defaultValue;
	if ( key === 'init' ) {
		acc[ current.key ] = current.initValue || current.defaultValue;
	} else if ( key === 'default' && 'initValue' in current && !( 'defaultValue' in current )){
		acc[ current.key ] = obj[ current.key ];
	}
	return acc;
}, {});
// 搜索
const searchData = ref( formatDefaultData({}, 'init' ));

// 分页
const pageInfo = ref({
	page: 1,
	pageSize: 20,
	total: 0
});

const tableData = ref([]);

const tableWrapRef = ref( null );
const tableRef = ref( null );
const loadingInstance = ref(null);
// 获取数据
const updateTableData = async () => {
	if ( loadingInstance.value ) {
		loadingInstance.value?.close();
	}
	loadingInstance.value = XlLoading.service({
		target: tableWrapRef.value,
		background: 'rgba(255,255,255,0.9)',
	});
	const { result, status, list } = await props.config.getData({ ...searchData.value, orderID: searchData.value?.orderID, ...pageInfo.value });
	if ( status === 'success' ){
		tableData.value = list;
		pageInfo.value = result.pageInfo;
		tableRef.value?.xlTableRef?.setScrollTop( 0 );
	}
	loadingInstance.value?.close();
};

// 搜索
const handleSearch = () => {
	updateTableData(); 
};

// 更多搜索
const isMore = ref( false );
const handleMore = () => {
	isMore.value = !isMore.value;
};
nextTick(() => {
	handleSearch();
});

// 重置
const handleReset = () => {
	searchData.value = formatDefaultData( searchData.value, 'default' );
	pageInfo.value.page = 1;
	handleSearch();
};

// 自定义列参数
const customColumns = ref({
	conf: [], // 所有可配置列（过滤不可见的）
	checked: [], // 所有选中列（包含不可取消的）
	visible: false // pop面板显示控制
});

const initCustomColumns = () => {
	// 自定义列的过滤和排序
	customColumns.value.conf = props.headers.filter(( v ) => !v.customColumnNoShow ).sort(( a, b ) => {
		if ( a.customColumnDisabled === b.customColumnDisabled ) {
			return a.sort - b.sort ? -1 : 1;
		} else {
			return a.customColumnDisabled ? -1 : 1;
		}
	}).
		map(( v ) => ({
			prop: v.prop,
			disabled: v.customColumnDisabled,
			label: v.label
		}));
	// 获取之前自定义列配置
	const customColumnProps = $getLocalStorage( `customColumnProps-${props.name}` );
	if ( customColumnProps ){
		customColumns.value.checked = unionBy( customColumnProps, customColumns.value.conf.filter(( v ) => v.disabled ).map(( v ) => v.prop ));
	} else {
		customColumns.value.checked = customColumns.value.conf.map(( v ) => v.prop );
	}
};
initCustomColumns();

// 显示自定义列面板
const showTableCustomColumns = () => {
	customColumns.value.visible = true;
};

// 保存自定义列设置
const submitCustomColumns = ( val ) => {
	XlMessage({ type: 'success', message: $t( 'messages.successEdit' ) });
	$setLocalStorage( `customColumnProps-${props.name}`, customColumns.value.checked );
	handleReset();
};

// 列表操作
const openOperation = ({ key, index }) => {
	const data = tableData.value[ index ];
	emits( 'operate', { key, index, data, list: tableData.value });
};

// 列表排序
const camelToUnderscore = ( camelCase ) => camelCase.replace( /([a-z])([A-Z])/g, '$1_$2' ).toLowerCase();
const changeTableSort = ( column ) => {
	const sortMap = {
		ascending: 'asc',
		descending: 'desc'
	};
	if ( column.prop && column.order ){
		searchData.value.sortBy = camelToUnderscore( column.prop );
		searchData.value.sortType = sortMap[ column.order ];
	} else {
		searchData.value.sortBy = undefined;
		searchData.value.sortType = undefined;
	}
	handleSearch();
};

defineExpose({ handleSearch, searchData });

</script>
<style lang="scss" scoped>
.custom-table {
	height: 100%;
	display: flex;
	flex-direction: column;
	.el-tabs {
		:deep(.el-tabs__nav-wrap) {
			padding-left: 0;
		}
	}
	.custom-table-wrap {
		flex: 1;
		height: 0;
		display: flex;
		flex-direction: column;
		position: relative;
		// flex-flow: column nowrap;
		.custom-table-header {
			display: flex;
			justify-content: space-between;
			.search-btns {
				flex: 1 0 auto;
				padding: 0 10px;
				position: relative;
				&:before {
					content: '';
					position: absolute;
					top: 5px;
					left: 0;
					width: 1px;
					height: 30px;
					background-color: #E0E4EA;
				}
			}
			.height-auto{
				height: auto;
			}
			.h48 {
				height: 48px;
				overflow: hidden;
			}
			.arrow-icon{
				width: 16px;
				height: 16px;
				margin-left: 4px;
			}
		}
		.custom-table-content {
			flex: 1;
			// height: 100%;
			height: calc(100% - 200px);
			display: flex;
		}
		.flex-full {
			flex: 1;
			height: 0;
		}
		:deep(.xl-table.el-table){
			// flex: 1;
			flex-grow: 1;
			position: relative;
		}
		:deep(.icon-chakansheji21) {
			font-size: 22px !important;
		}
		.custom-button {
			position: absolute;
			right: 0;
			top: 36px;
		}
		.xl-pagination{
			position: relative;
			margin: 0 -12px;
		}
	}
}
</style>
